/*
  Badges for «Мій профіль» (sections/profile.liquid → «Бейджі») and the gallery
  (sections/badges-gallery.liquid), loaded before assets/profile.js, which draws
  the panels and calls these two:

    GalasBadges.drawFace(canvas, { art, seed, title, number, variant })
      One card face, 358:448 like an Apple Wallet poster.

      The same rules as the passes and the PDF ticket,
      apps/tickets/supabase/functions/_shared/art/pattern.ts (posterSvg) —
      change one, change the other. One family: the season's motif on top, a
      deep band with the title and the big number below, the site's sparkle as
      the one thing that shines. Per edition (SERIES below): #10 a sunflower
      head, Streetball the 3×3 court, #11 paper lanterns with vyshyvanka
      stitches, #12 the cross-stitched cloth of stars. A tour card keeps the
      structure with rings in place of the motif.

      seed     the ticket code for an owned card, the badge key for the others —
               so the same ticket looks the same on the pass and on the shelf.
      variant  'colour' (default) or 'outline': the same composition as thin
               strokes on the page's own background, like the «ink» band of the
               PDF — a badge that is not yours yet.

      Nothing of the seed is ever printed: no codes, no serials.

    GalasBadges.holo(node)
      The holographic tilt. A mouse or pen over the node tilts it toward the
      pointer (up to MAX_TILT) and slides the sheen and the glare after it; the
      values travel as CSS custom properties written once per frame. A touch
      screen gets a slow idle shimmer instead, and prefers-reduced-motion gets
      neither. The look itself is profile.css (.holo, .holo__sheen,
      .holo__glare). With { gyro: true } (the ambassador tier card) a phone
      steers it by its tilt; iOS asks first, see gyroAsk below.
*/
(function () {
  'use strict';
  if (window.GalasBadges) return;

  var W = 358;
  var H = 448;
  var MAX_TILT = 10;

  /* ── tokens ─────────────────────────────────────────────────────────── */
  function tokens() {
    var css = getComputedStyle(document.documentElement);
    function v(name, fallback) { return (css.getPropertyValue(name) || '').trim() || fallback; }
    return {
      blue: v('--color-bg-accent', '#2454ac'),
      navy: v('--color-bg-accent-deep', '#10244b'),
      yellow: v('--color-bg-cta-yellow', '#ffcf33'),
      pale: v('--color-bg-status-accent-strong', '#d6e1f6'),
      ink: v('--color-text-inverse-secondary', '#f2f6fc'),
      line: v('--color-accent-40', '#83a5e5'),
      display: v('--font-display', 'sans-serif')
    };
  }

  // One series per edition, as SERIES in pattern.ts: motif, field, band, accent
  // (the one thing that shines), thread (the rest of the motif), paper (lantern
  // paper), ink (title), num (the big number). #12 takes the site's own tokens;
  // the seasonal palettes are pattern.ts's hex values — change both together.
  var SERIES = {
    g12: function (c) { return { motif: 'stitch', field: c.blue, band: c.navy, accent: c.yellow, thread: c.pale, ink: c.ink, num: c.yellow }; },
    g10: function () { return { motif: 'sunflower', field: '#2f5a4b', band: '#16302a', accent: '#efc35f', thread: '#d9e7cd', ink: '#eef4e8', num: '#efc35f' }; },
    streetball: function () { return { motif: 'court', field: '#39434f', band: '#1d242d', accent: '#ee8a4f', thread: '#ebe6dc', ink: '#f2efe9', num: '#ee8a4f' }; },
    g11: function () { return { motif: 'lanterns', field: '#6a2d2c', band: '#331819', accent: '#f0b566', thread: '#e9d9c3', paper: '#f4e9d8', ink: '#f4e9d8', num: '#f4e9d8' }; },
    tour: function (c) { return { motif: 'rings', field: c.blue, band: c.navy, accent: c.yellow, thread: c.pale, ink: c.ink, num: c.yellow }; }
  };

  /* ── seeded randomness: pattern.ts `seeded`, to the bit ─────────────── */
  function seeded(seed) {
    var h = 2166136261;
    for (var ch of String(seed || 'galas')) {
      h ^= ch.charCodeAt(0);
      h = Math.imul(h, 16777619);
    }
    return function () {
      h += 0x6d2b79f5;
      var t = h;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* ── the motifs: pattern.ts, line for line ──────────────────────────────
     Each returns shapes in roles ({ d, paint, alpha, line, noInk } or
     { glow: [x, y, r] }) built from SVG path strings, with the random draws in
     the same order as pattern.ts, so a seed gives the same picture on the pass
     and on the shelf. */
  function f(n) { return n.toFixed(2); }

  function sparkle(x, y, r, rot) {
    var k = 0.18;
    var pts = [[0, -r], [k * r, -k * r], [r, 0], [k * r, k * r], [0, r], [-k * r, k * r], [-r, 0], [-k * r, -k * r]];
    var cs = Math.cos(rot || 0);
    var sn = Math.sin(rot || 0);
    var p = pts.map(function (q) { return [x + q[0] * cs - q[1] * sn, y + q[0] * sn + q[1] * cs]; });
    return 'M' + f(p[0][0]) + ' ' + f(p[0][1]) + 'Q' + f(p[1][0]) + ' ' + f(p[1][1]) + ' ' + f(p[2][0]) + ' ' + f(p[2][1]) +
      'Q' + f(p[3][0]) + ' ' + f(p[3][1]) + ' ' + f(p[4][0]) + ' ' + f(p[4][1]) + 'Q' + f(p[5][0]) + ' ' + f(p[5][1]) + ' ' + f(p[6][0]) + ' ' + f(p[6][1]) +
      'Q' + f(p[7][0]) + ' ' + f(p[7][1]) + ' ' + f(p[0][0]) + ' ' + f(p[0][1]) + 'Z';
  }

  function circle(x, y, r) {
    return 'M' + f(x - r) + ' ' + f(y) + 'a' + f(r) + ' ' + f(r) + ' 0 1 0 ' + f(2 * r) + ' 0a' + f(r) + ' ' + f(r) + ' 0 1 0 ' + f(-2 * r) + ' 0Z';
  }

  function inZone(z, w, h, x, y) { return x >= z.x0 * w && x <= z.x1 * w && y >= z.y0 * h && y <= z.y1 * h; }

  // #12 «Вишитий візерунок»: the cross-stitched cloth and one carollers' star.
  function stitch(w, h, u, rnd, z) {
    var ox = rnd() * u;
    var oy = rnd() * u;
    var cols = Math.ceil(w / u) + 2;
    var rows = Math.ceil(h / (u * 0.86)) + 2;
    var cells = [];
    for (var j = -1; j < rows; j++) {
      for (var i = -1; i < cols; i++) cells.push([i, j, i * u + ox + (j % 2 ? u / 2 : 0), j * u * 0.86 + oy]);
    }
    var zone = z ? cells.filter(function (cell) { return inZone(z, w, h, cell[2], cell[3]); }) : [];
    var pick = zone.length ? zone[Math.floor(rnd() * zone.length)] : null;
    var pale = '';
    var gold = '';
    cells.forEach(function (cell) {
      if (cell === pick) return;
      if ((cell[0] + cell[1]) % 3 === 0) gold += sparkle(cell[2], cell[3], u * 0.26);
      else pale += sparkle(cell[2], cell[3], u * 0.14);
    });
    var out = [{ d: pale, paint: 'thread', alpha: 0.35 }, { d: gold, paint: 'accent', alpha: 0.85 }];
    if (pick) {
      out.push({ glow: [pick[2], pick[3], u * 1.4] },
        { d: sparkle(pick[2], pick[3], u * 0.44) + sparkle(pick[2], pick[3], u * 0.28, Math.PI / 4), paint: 'accent' });
    }
    return out;
  }

  // #10 «Соняшник»: seeds on the golden angle, one honey spiral, the sparkle in the heart.
  function sunflower(w, h, u, rnd, z) {
    var zz = z || { x0: 0.3, x1: 0.7, y0: 0.3, y1: 0.7 };
    var cx = (zz.x0 + rnd() * (zz.x1 - zz.x0)) * w;
    var cy = (zz.y0 + rnd() * (zz.y1 - zz.y0)) * h;
    var turn = rnd() * Math.PI * 2;
    var golden = Math.PI * (3 - Math.sqrt(5));
    var c = u * 0.17;
    var R = u * 2.3;
    var n = Math.floor(Math.pow(R / c, 2));
    var seeds = '';
    var arm = '';
    var rim = '';
    for (var i = 4; i <= n; i++) {
      var r = c * Math.sqrt(i);
      var a = i * golden + turn;
      var x = cx + r * Math.cos(a);
      var y = cy + r * Math.sin(a);
      if (x < -u || x > w + u || y < -u || y > h + u) continue;
      var size = c * (0.2 + 0.2 * Math.sqrt(i / n));
      if (i % 13 === 0) arm += circle(x, y, size * 1.25);
      else if (i > n * 0.86) rim += circle(x, y, size);
      else seeds += circle(x, y, size);
    }
    var petals = '';
    var count = 34;
    for (var k = 0; k < count; k++) {
      var pa = turn + (k * Math.PI * 2) / count;
      var r0 = R + u * 0.12;
      var r1 = R + u * (0.75 + 0.25 * ((k * 7) % 3) / 2);
      var side = u * 0.16;
      var bx = cx + r0 * Math.cos(pa);
      var by = cy + r0 * Math.sin(pa);
      var tx = cx + r1 * Math.cos(pa);
      var ty = cy + r1 * Math.sin(pa);
      var mx = (bx + tx) / 2;
      var my = (by + ty) / 2;
      var nx = -Math.sin(pa) * side;
      var ny = Math.cos(pa) * side;
      petals += 'M' + f(bx) + ' ' + f(by) + 'Q' + f(mx + nx) + ' ' + f(my + ny) + ' ' + f(tx) + ' ' + f(ty) +
        'Q' + f(mx - nx) + ' ' + f(my - ny) + ' ' + f(bx) + ' ' + f(by) + 'Z';
    }
    return [
      { d: petals, paint: 'thread', alpha: 0.16 },
      { d: seeds, paint: 'thread', alpha: 0.4 },
      { d: rim, paint: 'thread', alpha: 0.62 },
      { d: arm, paint: 'accent', alpha: 0.9 },
      { glow: [cx, cy, u * 1.2] },
      { d: sparkle(cx, cy, u * 0.38), paint: 'accent' }
    ];
  }

  // Streetball «Майданчик»: the 3×3 court in chalk, a dotted throw, the swish.
  function court(w, h, u, rnd, z, angle) {
    var zz = z || { x0: 0.4, x1: 0.6, y0: 0.2, y1: 0.3 };
    var bx = (zz.x0 + rnd() * (zz.x1 - zz.x0)) * w;
    var by = (zz.y0 + rnd() * (zz.y1 - zz.y0)) * h;
    var c = Math.cos(angle);
    var s = Math.sin(angle);
    function xy(lx, ly) { return [bx + lx * c - ly * s, by + lx * s + ly * c]; }
    function P(lx, ly) { var q = xy(lx, ly); return f(q[0]) + ' ' + f(q[1]); }

    var grain = '';
    var grains = Math.round((w * h) / (u * u) * 6);
    for (var i = 0; i < grains; i++) grain += circle(rnd() * w, rnd() * h, u * (0.012 + rnd() * 0.02));

    var far = 3 * Math.max(w, h);
    var base = -u * 0.7;
    var key = u * 1.05;
    var ft = u * 2.5;
    var arc = u * 3.7;
    var lines = 'M' + P(-far, base) + 'L' + P(far, base) +
      'M' + P(-key, base) + 'L' + P(-key, ft) + 'L' + P(key, ft) + 'L' + P(key, base) +
      'M' + P(-key, ft) + 'A' + f(key) + ' ' + f(key) + ' 0 0 0 ' + P(key, ft) +
      'M' + P(-arc, base) + 'L' + P(-arc, 0) + 'A' + f(arc) + ' ' + f(arc) + ' 0 0 0 ' + P(arc, 0) + 'L' + P(arc, base);
    var dashed = 'M' + P(key, ft) + 'A' + f(key) + ' ' + f(key) + ' 0 0 0 ' + P(-key, ft);
    var board = 'M' + P(-u * 0.5, -u * 0.36) + 'L' + P(u * 0.5, -u * 0.36);

    var side = rnd() < 0.5 ? -1 : 1;
    var lx = side * u * (0.8 + rnd() * 2.4);
    var ly = u * (2.5 + rnd() * 1.3);
    var kx = lx * 0.5 + side * u * (1 + rnd() * 1.2);
    var ky = ly * 0.5 - u * 0.6;
    var dots = '';
    for (var k = 1; k <= 10; k++) {
      var t = 0.12 + (k / 10) * 0.74;
      var q = xy(Math.pow(1 - t, 2) * lx + 2 * (1 - t) * t * kx, Math.pow(1 - t, 2) * ly + 2 * (1 - t) * t * ky);
      dots += circle(q[0], q[1], u * (0.07 - t * 0.035));
    }
    var ball = xy(lx, ly);
    var X = ball[0];
    var Y = ball[1];
    var br = u * 0.21;
    var seam = 'M' + f(X - br) + ' ' + f(Y) + 'L' + f(X + br) + ' ' + f(Y) + 'M' + f(X) + ' ' + f(Y - br) + 'L' + f(X) + ' ' + f(Y + br) +
      'M' + f(X - br * 0.7) + ' ' + f(Y - br * 0.7) + 'Q' + f(X - br * 0.2) + ' ' + f(Y) + ' ' + f(X - br * 0.7) + ' ' + f(Y + br * 0.7) +
      'M' + f(X + br * 0.7) + ' ' + f(Y - br * 0.7) + 'Q' + f(X + br * 0.2) + ' ' + f(Y) + ' ' + f(X + br * 0.7) + ' ' + f(Y + br * 0.7);
    return [
      { d: grain, paint: 'thread', alpha: 0.1, noInk: true },
      { d: lines, paint: 'thread', alpha: 0.42, line: u * 0.045 },
      { d: dashed, paint: 'thread', alpha: 0.2, line: u * 0.03 },
      { d: board, paint: 'thread', alpha: 0.7, line: u * 0.07 },
      { d: dots, paint: 'accent', alpha: 0.8 },
      { d: circle(X, Y, br), paint: 'accent', alpha: 0.95 },
      { d: seam, paint: 'field', alpha: 0.55, line: u * 0.025 },
      { glow: [bx, by, u * 1.1] },
      { d: circle(bx, by, u * 0.26), paint: 'accent', alpha: 0.7, line: u * 0.05 },
      { d: sparkle(bx, by, u * 0.5), paint: 'accent' }
    ];
  }

  // #11 «Ліхтарики»: paper lanterns with vyshyvanka stitches, one lit.
  function lanterns(w, h, u, rnd, z, drop) {
    var strings = ['', ''];
    var wires = ['', ''];
    var rows = [[], []];
    var depth = [0.62, 1];
    [0, 1].forEach(function (row) {
      var k = depth[row];
      var y0 = h * (0.02 + rnd() * 0.14);
      var y1 = h * (0.02 + rnd() * 0.14);
      var sag = u * (0.25 + rnd() * 0.45) * k;
      var x0 = -0.05 * w;
      var x1 = 1.05 * w;
      var mx = (x0 + x1) / 2;
      var my = (y0 + y1) / 2 + 2 * sag;
      wires[row] = 'M' + f(x0) + ' ' + f(y0) + 'Q' + f(mx) + ' ' + f(my) + ' ' + f(x1) + ' ' + f(y1);
      var n = Math.max(3, Math.round(w / (u * 1.35 * k)));
      for (var j = 0; j < n; j++) {
        var t = (j + 0.5 + (rnd() - 0.5) * 0.5) / n;
        var px = Math.pow(1 - t, 2) * x0 + 2 * (1 - t) * t * mx + t * t * x1;
        var py = Math.pow(1 - t, 2) * y0 + 2 * (1 - t) * t * my + t * t * y1;
        var r = u * k * (0.3 + rnd() * 0.24);
        var len = k * (u * 0.2 + rnd() * h * drop);
        var cross = rnd() < 0.5;
        strings[row] += 'M' + f(px) + ' ' + f(py) + 'L' + f(px) + ' ' + f(py + len);
        rows[row].push({ x: px, y: py + len + r, r: r, top: py + len, cross: cross });
      }
    });
    var front = rows[1];
    var zone = z ? front.filter(function (l) { return inZone(z, w, h, l.x, l.y); }) : [];
    var lit = zone.length ? zone[Math.floor(rnd() * zone.length)] : null;

    function body(l) { return circle(l.x, l.y, l.r); }
    function ribs(l) {
      var x = l.x, y = l.y, r = l.r;
      return 'M' + f(x - r * 0.93) + ' ' + f(y - r * 0.36) + 'Q' + f(x) + ' ' + f(y - r * 0.16) + ' ' + f(x + r * 0.93) + ' ' + f(y - r * 0.36) +
        'M' + f(x - r * 0.93) + ' ' + f(y + r * 0.36) + 'Q' + f(x) + ' ' + f(y + r * 0.56) + ' ' + f(x + r * 0.93) + ' ' + f(y + r * 0.36);
    }
    function cap(l) {
      return 'M' + f(l.x - l.r * 0.3) + ' ' + f(l.top - l.r * 0.06) + 'h' + f(l.r * 0.6) + 'v' + f(l.r * 0.2) + 'h' + f(-l.r * 0.6) + 'Z';
    }
    function mark(l) {
      var x = l.x, y = l.y, a = l.r * 0.4;
      if (l.cross) {
        var b = a * 0.3;
        return 'M' + f(x - b) + ' ' + f(y - a) + 'h' + f(2 * b) + 'v' + f(a - b) + 'h' + f(a - b) + 'v' + f(2 * b) + 'h' + f(b - a) + 'v' + f(a - b) + 'h' + f(-2 * b) +
          'v' + f(b - a) + 'h' + f(b - a) + 'v' + f(-2 * b) + 'h' + f(a - b) + 'Z';
      }
      return 'M' + f(x) + ' ' + f(y - a) + 'L' + f(x + a) + ' ' + f(y) + 'L' + f(x) + ' ' + f(y + a) + 'L' + f(x - a) + ' ' + f(y) + 'Z';
    }
    function hole(l) {
      if (l.cross) return '';
      var x = l.x, y = l.y, a = l.r * 0.16;
      return 'M' + f(x) + ' ' + f(y - a) + 'L' + f(x + a) + ' ' + f(y) + 'L' + f(x) + ' ' + f(y + a) + 'L' + f(x - a) + ' ' + f(y) + 'Z';
    }
    function all(list, fn) { return list.map(fn).join(''); }

    var plain = front.filter(function (l) { return l !== lit; });
    var out = [
      { d: wires[0], paint: 'thread', alpha: 0.28, line: u * 0.025, noInk: true },
      { d: strings[0], paint: 'thread', alpha: 0.28, line: u * 0.02, noInk: true },
      { d: all(rows[0], body), paint: 'paper', alpha: 0.32, noInk: true },
      { d: wires[1], paint: 'thread', alpha: 0.55, line: u * 0.03 },
      { d: strings[1], paint: 'thread', alpha: 0.55, line: u * 0.025 }
    ];
    if (lit) out.push({ glow: [lit.x, lit.y, lit.r * 3.2] });
    out.push(
      { d: all(plain, body), paint: 'paper' },
      { d: all(front, cap), paint: 'thread', alpha: 0.85 },
      { d: all(front, ribs), paint: 'field', alpha: 0.16, line: u * 0.02 },
      { d: all(plain, mark), paint: 'field', alpha: 0.9 },
      { d: all(plain, hole), paint: 'paper' }
    );
    if (lit) {
      out.push(
        { d: body(lit), paint: 'accent' },
        { d: ribs(lit), paint: 'field', alpha: 0.16, line: u * 0.02 },
        { d: sparkle(lit.x, lit.y, lit.r * 0.62), paint: 'field', alpha: 0.9 }
      );
    }
    return out;
  }

  // A tour: one sound, travelling — rings around a seeded point.
  function rings(w, h, rnd) {
    var cx = w * (0.3 + rnd() * 0.4);
    var cy = h * (0.35 + rnd() * 0.3);
    var step = w / 16;
    var out = [];
    for (var i = 12; i >= 1; i--) {
      var accent = i % 3 === 0;
      out.push({ d: circle(cx, cy, i * step), paint: accent ? 'accent' : 'thread', alpha: accent ? 0.85 : 0.35, line: accent ? 3 : 2 });
    }
    out.push({ d: circle(cx, cy, step * 0.35), paint: 'accent' });
    return out;
  }

  function motif(s, w, h, rnd) {
    var u = w / 6.5;
    var z = { x0: 0.15, x1: 0.85, y0: 0.18, y1: 0.7 };
    if (s.motif === 'sunflower') return sunflower(w, h, u, rnd, z);
    if (s.motif === 'court') return court(w, h, u, rnd, z, 0);
    if (s.motif === 'lanterns') return lanterns(w, h, u, rnd, z, 0.6);
    if (s.motif === 'rings') return rings(w, h, rnd);
    return stitch(w, h, u, rnd, z);
  }

  function withAlpha(hex, alpha) {
    var m = /^#?([0-9a-f]{6})$/i.exec(hex);
    if (!m) return hex;
    var n = parseInt(m[1], 16);
    return 'rgba(' + (n >> 16) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + alpha + ')';
  }

  // The shapes in colour, or (outline) as thin strokes in the current stroke
  // style, like the PDF's ink band: lantern paper is cut out of the strokes
  // behind it, glows and grain are left out.
  function paintShapes(ctx, shapes, s, outline) {
    shapes.forEach(function (sh) {
      if (sh.glow) {
        if (outline) return;
        var x = sh.glow[0], y = sh.glow[1], r = sh.glow[2];
        var g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, withAlpha(s.accent, 0.5));
        g.addColorStop(1, withAlpha(s.accent, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      if (!sh.d || (outline && sh.noInk)) return;
      var path = new Path2D(sh.d);
      if (outline) {
        if (sh.paint === 'paper' && !sh.line) {
          ctx.save();
          ctx.globalCompositeOperation = 'destination-out';
          ctx.fill(path);
          ctx.restore();
        }
        ctx.stroke(path);
        return;
      }
      var colour = sh.paint === 'paper' ? (s.paper || s.thread) : s[sh.paint];
      ctx.globalAlpha = sh.alpha === undefined ? 1 : sh.alpha;
      if (sh.line) {
        ctx.strokeStyle = colour;
        ctx.lineWidth = sh.line;
        ctx.lineCap = 'round';
        ctx.stroke(path);
      } else {
        ctx.fillStyle = colour;
        ctx.fill(path);
      }
      ctx.globalAlpha = 1;
    });
  }

  // Text that never runs off the card: the size shrinks to fit `max`.
  function fitText(ctx, c, text, x, y, size, max, stroke) {
    var px = size;
    ctx.font = '500 ' + px + 'px ' + c.display;
    var width = ctx.measureText(text).width;
    if (width > max) { px = Math.max(10, Math.floor(px * max / width)); ctx.font = '500 ' + px + 'px ' + c.display; }
    if (stroke) ctx.strokeText(text, x, y); else ctx.fillText(text, x, y);
  }

  function paintFace(ctx, opts) {
    var c = tokens();
    var s = (SERIES[opts.art] || SERIES.g12)(c);
    var outline = opts.variant === 'outline';
    var rnd = seeded(opts.seed);
    var bandTop = H * 0.54;
    ctx.clearRect(0, 0, W, H);
    ctx.textBaseline = 'alphabetic';
    if (outline) {
      ctx.strokeStyle = c.line;
      ctx.fillStyle = c.line;
      ctx.lineWidth = 1;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
    } else {
      ctx.fillStyle = s.field;
      ctx.fillRect(0, 0, W, H);
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, W, bandTop);
    ctx.clip();
    paintShapes(ctx, motif(s, W, bandTop, rnd), s, outline);
    ctx.restore();
    if (!outline) {
      // pattern.ts `calm`: the top of the poster stays quiet under Wallet's header.
      var calm = ctx.createLinearGradient(0, 0, 0, H * 0.15);
      calm.addColorStop(0, withAlpha(s.field, 0.85));
      calm.addColorStop(1, withAlpha(s.field, 0));
      ctx.fillStyle = calm;
      ctx.fillRect(0, 0, W, H * 0.15);
    }

    if (outline) {
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      ctx.moveTo(0, bandTop);
      ctx.lineTo(W, bandTop);
      ctx.stroke();
      ctx.fillStyle = c.line;
      fitText(ctx, c, opts.title || '', W * 0.06, H * 0.615, W * 0.05, W * 0.88, false);
      ctx.lineWidth = 1.5;
      fitText(ctx, c, opts.number || '', W * 0.05, H * 0.83, W * 0.3, W * 0.9, true);
      return;
    }
    ctx.fillStyle = s.band;
    ctx.fillRect(0, bandTop, W, H - bandTop);
    ctx.fillStyle = s.ink;
    fitText(ctx, c, opts.title || '', W * 0.06, H * 0.615, W * 0.05, W * 0.88, false);
    ctx.fillStyle = s.num;
    fitText(ctx, c, opts.number || '', W * 0.05, H * 0.83, W * 0.3, W * 0.9, false);
  }

  function drawFace(canvas, opts) {
    var ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx || typeof Path2D !== 'function') return;
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    function paint() {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintFace(ctx, opts);
    }
    paint();
    // The display font may still be on its way; draw again once it is here.
    if (document.fonts && document.fonts.load) {
      document.fonts.load('500 64px ' + tokens().display).then(paint, function () {});
    }
  }

  /* ── holographic tilt ───────────────────────────────────────────────── */
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var finePointer = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : { matches: true };

  // `x`, `y` are the light's position on the node, 0…1. Badges read the
  // percentages and degrees; the tier card reads the bare --hx/--hy as well.
  function aim(node, x, y, tilt) {
    node.style.setProperty('--holo-rx', ((0.5 - y) * 2 * tilt).toFixed(2) + 'deg');
    node.style.setProperty('--holo-ry', ((x - 0.5) * 2 * tilt).toFixed(2) + 'deg');
    node.style.setProperty('--holo-mx', (x * 100).toFixed(1) + '%');
    node.style.setProperty('--holo-my', (y * 100).toFixed(1) + '%');
    node.style.setProperty('--hx', x.toFixed(3));
    node.style.setProperty('--hy', y.toFixed(3));
    node.style.setProperty('--holo-o', '1');
  }
  var AIMED = ['--holo-rx', '--holo-ry', '--holo-mx', '--holo-my', '--hx', '--hy', '--holo-o'];
  function rest(node) { AIMED.forEach(function (p) { node.style.removeProperty(p); }); }

  /* Phone tilt (opts.gyro). One listener for every node that asked. The angle
     the phone is held at when the first reading arrives is "straight"; it then
     follows the hand slowly, so the card answers to a turn of the wrist, not
     to how the phone happens to be held. Safari asks first
     (DeviceOrientationEvent.requestPermission, only from a tap): until then,
     and if the answer is no, the card keeps its idle drift. */
  var GYRO_RANGE = 22;   // degrees of turn from "straight" to the card's edge
  var gyro = { nodes: [], on: false, base: null, x: 0.5, y: 0.5, frame: 0 };
  var gyroAsks = typeof window.DeviceOrientationEvent !== 'undefined' &&
    typeof window.DeviceOrientationEvent.requestPermission === 'function';

  function onOrient(e) {
    if (e.beta == null || e.gamma == null) return;
    var angle = (screen.orientation && screen.orientation.angle) || window.orientation || 0;
    var side = e.gamma;
    var fwd = e.beta;
    if (angle === 90) { side = e.beta; fwd = -e.gamma; }
    else if (angle === -90 || angle === 270) { side = -e.beta; fwd = e.gamma; }
    if (!gyro.base) gyro.base = { side: side, fwd: fwd };
    gyro.base.side += (side - gyro.base.side) * 0.015;
    gyro.base.fwd += (fwd - gyro.base.fwd) * 0.015;
    var dx = Math.max(-1, Math.min(1, (side - gyro.base.side) / GYRO_RANGE));
    var dy = Math.max(-1, Math.min(1, (fwd - gyro.base.fwd) / GYRO_RANGE));
    gyro.x = 0.5 + dx / 2;
    gyro.y = 0.5 + dy / 2;
    if (!gyro.frame) gyro.frame = requestAnimationFrame(gyroPaint);
  }
  function gyroPaint() {
    gyro.frame = 0;
    gyro.nodes.forEach(function (n) {
      if (!n.isConnected || n.__holoHidden) return;
      if (!n.classList.contains('is-holo')) { n.classList.remove('holo--idle'); n.classList.add('is-holo'); }
      aim(n, gyro.x, gyro.y, n.__holoTilt);
    });
  }
  function gyroStart() {
    if (gyro.on) return;
    gyro.on = true;
    window.addEventListener('deviceorientation', onOrient);
    gyro.nodes.forEach(function (n) { if (n.__holoAsk) n.__holoAsk.hidden = true; });
  }
  function gyroAsk() {
    window.DeviceOrientationEvent.requestPermission().then(function (answer) {
      if (answer === 'granted') gyroStart();
      gyro.nodes.forEach(function (n) { if (n.__holoAsk) n.__holoAsk.hidden = true; });
    }, function () {});
  }

  /* holo(node, opts)
       opts.tilt  the most the node turns, degrees (MAX_TILT)
       opts.gyro  follow the phone's tilt on a touch screen
       opts.ask   with gyro on iOS: a button that asks for the tilt; it is
                  shown only there, and a tap on the node asks as well */
  function holo(node, opts) {
    if (!node || node.__holo) return;
    // Also called straight from forEach, where the second argument is an index.
    opts = opts && typeof opts === 'object' ? opts : {};
    node.__holo = true;
    node.__holoTilt = opts.tilt || MAX_TILT;
    node.classList.add('holo');
    if (reduce.matches) return;
    if (!finePointer.matches) {
      node.classList.add('holo--idle');
      // Not all in step: each card starts its drift at a different point.
      node.style.setProperty('--holo-delay', (-Math.random() * 8).toFixed(2) + 's');
      if (!opts.gyro || !('DeviceOrientationEvent' in window)) return;
      gyro.nodes.push(node);
      // Off screen, the card is left alone rather than repainted for nothing.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          node.__holoHidden = !entries[0].isIntersecting;
        }).observe(node);
      }
      if (!gyroAsks) { gyroStart(); return; }
      if (gyro.on) return;
      if (opts.ask) {
        node.__holoAsk = opts.ask;
        opts.ask.hidden = false;
        opts.ask.addEventListener('click', gyroAsk);
      }
      node.addEventListener('click', function () { if (!gyro.on) gyroAsk(); });
      return;
    }

    var frame = 0;
    var px = 0.5;
    var py = 0.5;
    function apply() {
      frame = 0;
      aim(node, px, py, node.__holoTilt);
    }
    node.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      var box = node.getBoundingClientRect();
      if (!box.width || !box.height) return;
      px = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
      py = Math.min(1, Math.max(0, (e.clientY - box.top) / box.height));
      node.classList.add('is-holo');
      if (!frame) frame = requestAnimationFrame(apply);
    });
    node.addEventListener('pointerleave', function () {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      node.classList.remove('is-holo');
      rest(node);
    });
  }

  window.GalasBadges = { drawFace: drawFace, holo: holo };
})();
